/**
 * /verify-email?token=...
 *
 * Página pública — no requiere login.
 * Valida el token con el backend y, si el usuario está logueado,
 * actualiza Firestore directamente. Si no está logueado, muestra
 * un aviso para que inicie sesión (sincronización automática al próximo login).
 */
import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { db } from "@/firebase";
import { doc, setDoc } from "firebase/firestore";
import { useAuth } from "@/contexts/AuthContext";
import { CheckCircle, XCircle, Loader2, MailCheck, AlertTriangle } from "lucide-react";

const RAW_BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const BACKEND_URL = RAW_BACKEND_URL === "http://localhost:8001" && window.location.hostname !== "localhost"
  ? ""
  : RAW_BACKEND_URL;

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [status, setStatus] = useState("loading"); // loading | success | already | error
  const [errorCode, setErrorCode] = useState(null);
  const [verifiedEmail, setVerifiedEmail] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    const token = params.get("token");
    if (!token) {
      setStatus("error");
      setErrorCode("missing");
      setErrorMsg("No se encontró el token de verificación en el link.");
      return;
    }

    async function verify() {
      try {
        const res = await fetch(`${BACKEND_URL}/api/email/verify?token=${encodeURIComponent(token)}`);
        const data = await res.json();

        if (!data.ok) {
          setStatus("error");
          setErrorCode(data.code || "unknown");
          setErrorMsg(data.error || "Error al verificar el token.");
          return;
        }

        setVerifiedEmail(data.email);

        // Si el usuario está logueado, actualizar Firestore
        if (user && user.uid === data.uid && !user.isDemo) {
          try {
            await setDoc(doc(db, "users", data.uid), {
              personalEmail: data.email,
              personalEmailVerified: true,
            }, { merge: true });
          } catch (_) {
            // Fallo silencioso — se sincronizará al próximo login via check-verified
          }
        }

        setStatus(data.already_verified ? "already" : "success");
      } catch (_) {
        setStatus("error");
        setErrorCode("network");
        setErrorMsg("No se pudo conectar con el servidor. Intenta de nuevo.");
      }
    }

    verify();
  }, [params, user]);

  return (
    <div className="min-h-screen flex items-center justify-center px-4"
      style={{ background: "#F1F5F9" }}>
      <div className="w-full max-w-md">

        {/* Header */}
        <div className="text-center mb-6">
          <div className="text-xs font-bold uppercase tracking-widest mb-1"
            style={{ color: "#00628F", letterSpacing: "3px" }}>WE 360</div>
          <p className="text-xs" style={{ color: "#94A3B8" }}>Tu salud, tu camino</p>
        </div>

        <div className="bg-white rounded-2xl overflow-hidden shadow-sm"
          style={{ border: "1px solid #E2E8F0" }}>

          {/* Loading */}
          {status === "loading" && (
            <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
              <Loader2 size={40} className="animate-spin mb-4" style={{ color: "#00628F" }} />
              <p className="text-sm font-medium" style={{ color: "#1E293B" }}>Verificando tu correo...</p>
              <p className="text-xs mt-1" style={{ color: "#94A3B8" }}>Esto tomará solo un momento</p>
            </div>
          )}

          {/* Success */}
          {(status === "success" || status === "already") && (
            <>
              <div className="px-6 py-8 text-center">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                  style={{ background: "#DCFCE7" }}>
                  <CheckCircle size={32} style={{ color: "#16A34A" }} />
                </div>
                <h1 className="text-xl font-bold mb-2" style={{ color: "#1E293B" }}>
                  {status === "already" ? "Email ya verificado" : "¡Email verificado!"}
                </h1>
                {verifiedEmail && (
                  <div className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl mb-3"
                    style={{ background: "#F0FDF4", border: "1px solid #BBF7D0" }}>
                    <MailCheck size={15} style={{ color: "#16A34A" }} />
                    <span className="text-sm font-semibold" style={{ color: "#166534" }}>
                      {verifiedEmail}
                    </span>
                  </div>
                )}
                <p className="text-sm" style={{ color: "#475569" }}>
                  {status === "already"
                    ? "Este correo ya estaba verificado en tu cuenta."
                    : "Tu email personal quedó confirmado como método de recuperación de cuenta."}
                </p>

                {/* Si no está logueado, aviso de sincronización */}
                {!user && (
                  <div className="mt-4 px-4 py-3 rounded-xl text-left"
                    style={{ background: "#FFFBEB", border: "1px solid #FCD34D" }}>
                    <div className="flex items-start gap-2">
                      <AlertTriangle size={14} style={{ color: "#D97706", flexShrink: 0, marginTop: 2 }} />
                      <p className="text-xs" style={{ color: "#92400E" }}>
                        <strong>Inicia sesión</strong> para activar el email verificado en tu perfil.
                        Se activará automáticamente al próximo login.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <div className="px-6 pb-6 flex flex-col gap-2">
                {user ? (
                  <button
                    onClick={() => navigate("/mi-cuenta")}
                    className="w-full py-3 rounded-xl text-sm font-semibold"
                    style={{ background: "#00628F", color: "white", border: "none", cursor: "pointer" }}
                    data-testid="verify-email-go-cuenta"
                  >
                    Ver Mi Cuenta
                  </button>
                ) : (
                  <button
                    onClick={() => navigate("/")}
                    className="w-full py-3 rounded-xl text-sm font-semibold"
                    style={{ background: "#00628F", color: "white", border: "none", cursor: "pointer" }}
                    data-testid="verify-email-go-login"
                  >
                    Iniciar sesión
                  </button>
                )}
                <button
                  onClick={() => navigate("/dashboard")}
                  className="w-full py-3 rounded-xl text-sm"
                  style={{ background: "#F1F5F9", color: "#64748B", border: "none", cursor: "pointer" }}
                  data-testid="verify-email-go-dashboard"
                >
                  Ir al Dashboard
                </button>
              </div>
            </>
          )}

          {/* Error */}
          {status === "error" && (
            <>
              <div className="px-6 py-8 text-center">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                  style={{ background: "#FEE2E2" }}>
                  <XCircle size={32} style={{ color: "#DC2626" }} />
                </div>
                <h1 className="text-xl font-bold mb-2" style={{ color: "#1E293B" }}>
                  {errorCode === "expired" ? "Enlace expirado" :
                   errorCode === "superseded" ? "Enlace reemplazado" :
                   errorCode === "missing" ? "Enlace inválido" :
                   "No se pudo verificar"}
                </h1>
                <p className="text-sm" style={{ color: "#475569" }}>
                  {errorMsg}
                </p>
              </div>
              <div className="px-6 pb-6 flex flex-col gap-2">
                <button
                  onClick={() => navigate("/mi-cuenta")}
                  className="w-full py-3 rounded-xl text-sm font-semibold"
                  style={{ background: "#00628F", color: "white", border: "none", cursor: "pointer" }}
                  data-testid="verify-email-retry-btn"
                >
                  Solicitar nuevo enlace
                </button>
                <button
                  onClick={() => navigate("/dashboard")}
                  className="w-full py-3 rounded-xl text-sm"
                  style={{ background: "#F1F5F9", color: "#64748B", border: "none", cursor: "pointer" }}
                >
                  Volver al Dashboard
                </button>
              </div>
            </>
          )}

        </div>
      </div>
    </div>
  );
}
